import React, { useEffect, useRef, useState } from "react";
import { useLocale } from "../../contexts/localeContext";
import { deleteImage } from "../../services/enrolmentService";
import TrashIconBlue from "../../images/Delete.svg";
import { toast } from "react-toastify";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { getErrorMessage, showError } from "../../services/utilities/ServiceError";
import { useDialog } from "../dialog/DialogContext";
import Camera from "../../images/Camera.svg";
import { getEnrollResultCommandType } from "../../services/wsPluginService/plugins/decodePluginMsg";
import enrollCmd from "../../services/wsPluginService/plugins/base/enrollCmd";
import { decodeCameraPluginMsg } from "../../services/wsPluginService/plugins/cameraDecoder";
import { IPluginCameraImage } from "../../services/wsPluginService/plugins/base/cameraModels";

const DialogContent = ({ onNext, selectedImage, handleImageDelete, setUploadNewImage, pluginWSInstance, setBusyScanning }) => {
    const drop = useRef(null) as any;
    const { translate } = useLocale();
    const [newImage, setNewImage] = useState("") as any
    const [imageName, setImageName] = useState("") as any
    const [imageSize, setImageSize] = useState("") as any
    const [isIconHovered, setIconHovered] = useState(false);
    const dialog = useDialog();

    useEffect(() => {

        drop.current.addEventListener('dragover', handleDragOver);
        drop.current.addEventListener('drop', handleDrop);

        return () => {
            drop.current.removeEventListener('dragover', handleDragOver);
            drop.current.removeEventListener('drop', handleDrop);
        };
    }, []);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files[0]) {
            await getBase64(e.dataTransfer.files[0]).then((base64String) => {
                setNewImage(base64String)
            }).catch((error) => showError(error, toast.warn));
        }
    };

    const generateFormattedImageSize = (bytes, decimals = 2) => {
        if (bytes === 0) return "0 Bytes"
        const k = 1024
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
    }

    const getBase64 = file => {
        setImageName(file.name)
        setImageSize(generateFormattedImageSize(file.size))
        return new Promise(resolve => {
            let baseURL;
            let reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                baseURL = reader.result;
                if (file.type === 'image/png') {
                    resolve(baseURL.slice(22));
                }
                else if (file.type === 'image/jpeg') {
                    resolve(baseURL.slice(23));
                }
                else {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.unsupportedImage) })
                }
            };
        });
    };

    useEffect(() => {
        setNewImage('')
        setImageName('')
    }, [selectedImage.id])

    useEffect(() => {
        if (newImage) {
            setUploadNewImage({ ...selectedImage, image: newImage, id: selectedImage.id }, setNewImage, setImageName)
        }
    }, [newImage])

    const handleChange = async (e) => {
        await setNewImage(await getBase64(e.target.files[0]))
    }

    const handleDelete = async () => {
        const clearCurrentImage = () => {
            deleteImage(selectedImage.masterImageId).then(() => {
                setNewImage('')
                setImageName('')
                handleImageDelete(selectedImage)
            }).catch((err) => {
                console.log(err)
            })
        }
        newImage ? clearCurrentImage() :
            await deleteImage(selectedImage.masterImageId).then(() => {
                setNewImage('')
                setImageName('')
                handleImageDelete(selectedImage)
            }).catch((err) => {
                console.log(err)
            })
    }

    const openImageCapture = () => { 
        if (pluginWSInstance && pluginWSInstance?.readyState === WebSocket.OPEN) {
            // open webcam
            setBusyScanning(true)   // Focus the capture wizard, block interactions with the rest of the system 

            pluginWSInstance?.send("{'enrollType':'camera','type':'capture'}");    
            pluginWSInstance.onmessage = (value) => {        
                onMessagePluginInstance(value.data)
            };
        }
    }

    // receive, process, store image from webcam
    const onMessagePluginInstance = (response: any) => {
        if (response.toLowerCase().includes("statusmsg=error"))     // Cancel Capture for any reason - closing webcam before image is captured
        {
            setBusyScanning(false)
        }  

        else if (getEnrollResultCommandType(response) === enrollCmd.CMD_CAMERA) {
            const cameraImage = decodeCameraPluginMsg(response) as IPluginCameraImage;
            const trimmedBase64 = cameraImage.image.substring(cameraImage.image.indexOf(",") + 1);    // cameraImage.image is already in base64 format, no need to call getBase64 method
            setUploadNewImage({ ...selectedImage, image: trimmedBase64, id: selectedImage.id }, setNewImage, setImageName)  //save new image
        }
        setBusyScanning(false)
    }

    return (
        <>
            <div className="pt-3 pr-3 h-full relative w-full" style={{overflow:"auto"}}>
                <div className="px-3">
                    <h3 className="text-base leading-6 font-medium text-gray-900">{translate.byKey(TranslationKeys.uploadProfileImage)}</h3>
                    {imageName && <p className="text-xs text-gray-800">
                        <a href="#">{translate.byKey(TranslationKeys.uploaded)}</a> {imageName} {imageSize}
                    </p>}
                     <label ref={drop} style={{ maxWidth: '460px' }} htmlFor="file-upload" className={`mt-1 object-contain bg-gray-bar max-w-full  h-64 flex justify-center px-6 pt-5  border-2  border-dashed rounded-sm ${newImage || selectedImage.image ? null : 'border-gray-deselect'}`}>
                        <div className={selectedImage && selectedImage.image ? "space-y-1 text-center" : "space-y-1 text-center mt-16"}>
                            {selectedImage?.image || newImage ? (<img className=" max-w-full h-full object-contain"
                                src={`data:image/png;base64,${newImage ? newImage : selectedImage.image}`}
                                alt="user" />) : <a href="#"> <label
                                    htmlFor="file-upload">       <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 48 48"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div className="flex text-sm text-gray-600">
                                        <label
                                            htmlFor="file-upload"
                                            className="relative cursor-pointer  rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                                        >
                                            <p className="text-md text-gray-800">{translate.byKey(TranslationKeys.dragAndDropDialogText)}</p>
                                            <input onChange={handleChange} id="file-upload" name="file-upload" type="file" className="sr-only" />
                                        </label>
                                    </div>
                                </label>
                                </a>}
                        </div>
                    </label>
                    <div className="-ml-4 mt-2 flex justify-end items-center flex-wrap sm:flex-nowrap">
                        <div className="ml-4 mt-4 flex-shrink-0 flex"> 
                            <img
                                src={Camera}
                                onClick={openImageCapture}
                                alt="Image Capture"
                                style={{cursor: "pointer"}}
                                className={`px-2 h-7 py-1 hover:text-indigo-500`}
                            /> 
                        </div>
                        <div className="ml-1 mt-4 flex-shrink-0 flex" >
                            {(selectedImage.image || newImage) && <a href='#'>
                                {isIconHovered ? <img
                                    onMouseEnter={() => setIconHovered(true)}
                                    onMouseLeave={() => setIconHovered(false)}
                                    onClick={() => handleDelete()}
                                    src={TrashIconBlue}
                                    className="px-2 h-7 py-1 hover:text-indigo-500"
                                    alt="" /> : <img
                                        onMouseEnter={() => setIconHovered(true)}
                                        onMouseLeave={() => setIconHovered(false)}
                                        onClick={() => handleDelete()}
                                        src={TrashIconBlue}
                                        className={`px-2 h-7 py-1 hover:text-indigo-500`}
                                        alt="" />}

                            </a>}
                        </div>
                    </div> 
                </div>
            </div>
        </>
    );
};

export default DialogContent;
